// Verificación en dos pasos en el panel de Usuarios del administrador (MT-043-C): su propia cuenta (estado, activarla, códigos de respaldo nuevos) y el
// informe de cobertura del consultorio por perfil (con la política vigente y a quién falta invitar). El reinicio y el desbloqueo de OTRA cuenta están
// en la fila de cada usuario (UsuariosPage). Todo lo decide el servidor: aquí solo se muestra y se pide.
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { guardarSesionHceAdmin } from '@/auth/hceAdminSession';
import { fechaDePolitica } from '@/auth/politicaMfaTexto';
import { PasoEnrolamiento, VistaCodigosRespaldo } from '@/auth/PasosMfa';
import { loginHceAdmin } from '@/lib/hceVault';
import {
  descargarBlob,
  descargarCoberturaCsv,
  enrolarConfirmarAdmin,
  enrolarIniciarAdmin,
  obtenerCobertura,
  sesionAdmin,
  type CodigosRespaldo,
  type EstadoMfa,
  type InformeCobertura,
} from '@/lib/hceMfa';

type EstadoAdmin = EstadoMfa & { usuario?: string };

export function SeguridadMfaPanel({ token, subdominio, onSesionNueva }: { token: string; subdominio: string; onSesionNueva: () => void }): JSX.Element {
  return (
    <div className="space-y-4">
      <MiSeguridadAdmin token={token} subdominio={subdominio} onSesionNueva={onSesionNueva} />
      <CoberturaMfa token={token} />
    </div>
  );
}

function MiSeguridadAdmin({ token, subdominio, onSesionNueva }: { token: string; subdominio: string; onSesionNueva: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<EstadoAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<'estado' | 'pedir-clave' | 'enrolar' | 'respaldo-pedir' | 'respaldo-nuevos'>('estado');
  const [clave, setClave] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [nuevos, setNuevos] = useState<CodigosRespaldo | null>(null);

  const cargar = useCallback(async () => {
    const r = await sesionAdmin(token).estado();
    if (r.ok) setEstado(r as EstadoAdmin);
    else setError(r.error);
  }, [token]);
  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Activarla por voluntad propia: se pide la contraseña de nuevo (el servidor entrega un token de enrolamiento solo con ella). */
  async function pedirEnrolamiento(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!estado?.usuario) return;
    setError(null);
    setEnviando(true);
    const r = await loginHceAdmin(subdominio, estado.usuario, clave, { enrolarMfa: true });
    setEnviando(false);
    setClave('');
    if (!r.ok && r.mfaEnroll) {
      setEnrollToken(r.mfaEnroll.token);
      setVista('enrolar');
      return;
    }
    setError(r.ok ? t('seguridad.admin.errorActivar') : r.error || t('auth.login.errorGenerico'));
  }

  async function pedirRespaldo(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const r = await sesionAdmin(token).regenerarRespaldo(codigo.trim());
    setEnviando(false);
    setCodigo('');
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setNuevos({ backupCodes: r.backupCodes, backupCodesPdf: r.backupCodesPdf });
    setVista('respaldo-nuevos');
  }

  const politica = estado ? fechaDePolitica(estado.politica) : null;
  const iniciar = useCallback(() => enrolarIniciarAdmin(enrollToken ?? ''), [enrollToken]);

  return (
    <section className="space-y-3 rounded-md border border-border bg-hce-surface p-4" aria-label={t('seguridad.admin.titulo')}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-hce-ink">{t('seguridad.admin.titulo')}</h2>
        {estado && <Badge variant={estado.activo ? 'sage' : 'clay'}>{estado.activo ? t('seguridad.activa') : t('seguridad.inactiva')}</Badge>}
      </div>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {estado && vista === 'estado' && (
        <>
          {politica && <p className="text-xs text-hce-ink">{t(politica.clave, politica.valores)}</p>}
          {estado.activo ? (
            <div className="flex items-center gap-3">
              <p className="text-xs text-hce-muted">{t('seguridad.respaldoRestantes', { n: estado.respaldoRestantes })}</p>
              <Button variant="outline" size="sm" onClick={() => { setError(null); setVista('respaldo-pedir'); }}>
                {t('seguridad.respaldoNuevos')}
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => { setError(null); setVista('pedir-clave'); }}>
              {t('seguridad.activar')}
            </Button>
          )}
        </>
      )}
      {vista === 'pedir-clave' && (
        <form onSubmit={(e) => void pedirEnrolamiento(e)} className="space-y-2">
          <p className="text-xs text-hce-muted">{t('seguridad.admin.pedirClave')}</p>
          <Input type="password" value={clave} onChange={(e) => setClave(e.target.value)} autoComplete="current-password" aria-label={t('auth.login.contrasena')} required />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={enviando || !clave}>
              {t('seguridad.admin.continuar')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setVista('estado')}>
              {t('auth.mfa.volver')}
            </Button>
          </div>
        </form>
      )}
      {vista === 'enrolar' && enrollToken && (
        <PasoEnrolamiento
          iniciar={iniciar}
          confirmar={(c) => enrolarConfirmarAdmin(enrollToken, c)}
          onCancelar={() => setVista('estado')}
          textoContinuar={t('seguridad.admin.terminar')}
          onTerminar={(r) => {
            // La sesión de solo contraseña dejó de valer al activarse el factor: la nueva (con factor verificado) la reemplaza.
            guardarSesionHceAdmin(r.token, subdominio);
            onSesionNueva();
          }}
        />
      )}
      {vista === 'respaldo-pedir' && (
        <form onSubmit={(e) => void pedirRespaldo(e)} className="space-y-2">
          <p className="text-xs text-hce-muted">{t('seguridad.respaldoNuevosAyuda')}</p>
          <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} autoComplete="one-time-code" maxLength={64} aria-label={t('auth.mfa.codigo.etiqueta')} required />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={enviando || !codigo.trim()}>
              {t('seguridad.respaldoGenerar')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setVista('estado')}>
              {t('auth.mfa.volver')}
            </Button>
          </div>
        </form>
      )}
      {vista === 'respaldo-nuevos' && nuevos && (
        <VistaCodigosRespaldo
          codigos={nuevos}
          onContinuar={() => {
            setNuevos(null);
            setVista('estado');
            void cargar();
          }}
        />
      )}
    </section>
  );
}

function CoberturaMfa({ token }: { token: string }): JSX.Element {
  const { t } = useTranslation();
  const [informe, setInforme] = useState<InformeCobertura | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);

  useEffect(() => {
    void obtenerCobertura(token).then((r) => (r.ok ? setInforme(r) : setError(r.error)));
  }, [token]);

  async function descargar(): Promise<void> {
    setDescargando(true);
    setError(null);
    const r = await descargarCoberturaCsv(token);
    setDescargando(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    descargarBlob(new Blob([r.csv], { type: 'text/csv;charset=utf-8' }), r.nombre);
  }

  const politica = informe ? fechaDePolitica({ obligatorio: informe.politica.obligatorioAhora, desde: informe.politica.desde, invalida: informe.politica.invalida }) : null;

  return (
    <section className="space-y-3 rounded-md border border-border bg-hce-surface p-4" aria-label={t('seguridad.cobertura.titulo')}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-hce-ink">{t('seguridad.cobertura.titulo')}</h2>
        <Button variant="outline" size="sm" disabled={descargando || !informe} onClick={() => void descargar()}>
          {descargando ? t('seguridad.cobertura.descargando') : t('seguridad.cobertura.descargar')}
        </Button>
      </div>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!informe && !error && <p className="text-xs text-hce-muted">{t('seguridad.cargando')}</p>}
      {informe && (
        <>
          {politica && <p className="text-xs text-hce-ink">{t(politica.clave, politica.valores)}</p>}
          {informe.politica.origen !== 'ninguna' && <p className="text-xs text-hce-muted">{t(`seguridad.cobertura.origen.${informe.politica.origen}`)}</p>}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('seguridad.cobertura.perfil')}</TableHead>
                <TableHead>{t('seguridad.cobertura.activas')}</TableHead>
                <TableHead>{t('seguridad.cobertura.con')}</TableHead>
                <TableHead>{t('seguridad.cobertura.sin')}</TableHead>
                <TableHead>{t('seguridad.cobertura.cobertura')}</TableHead>
                <TableHead>{t('seguridad.cobertura.politica')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {informe.perfiles.map((p) => (
                <TableRow key={p.rol}>
                  <TableCell>{p.etiqueta}</TableCell>
                  <TableCell className="font-mono">{p.activos}</TableCell>
                  <TableCell className="font-mono">{p.conFactor}</TableCell>
                  <TableCell className="font-mono">{p.sinFactor}</TableCell>
                  <TableCell className="font-mono">{p.cobertura === null ? '—' : `${p.cobertura}%`}</TableCell>
                  <TableCell>
                    <Badge variant={p.obligatorio ? 'pine' : 'secondary'}>
                      {p.obligatorio ? t('seguridad.cobertura.obligatoria') : p.alcanzadoPorLaPolitica ? t('seguridad.cobertura.alcanzada') : t('seguridad.cobertura.opcional')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="font-medium">{t('seguridad.cobertura.total')}</TableCell>
                <TableCell className="font-mono">{informe.total.activos}</TableCell>
                <TableCell className="font-mono">{informe.total.conFactor}</TableCell>
                <TableCell className="font-mono">{informe.total.sinFactor}</TableCell>
                <TableCell className="font-mono">{informe.total.cobertura === null ? '—' : `${informe.total.cobertura}%`}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
          {informe.pendientes.length > 0 && (
            <details className="text-xs text-hce-muted">
              <summary className="cursor-pointer">{t('seguridad.cobertura.pendientes', { n: informe.pendientes.length })}</summary>
              <ul className="mt-1 space-y-0.5">
                {informe.pendientes.map((c) => (
                  <li key={c.id}>
                    <span className="text-hce-ink">{c.nombre}</span> <span className="font-mono">{c.usuario}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
